import React, { useState } from "react";
import { HeaderAction, HeaderState } from "../../../types";
import { renderLayoutPreview } from "../../../utils/layoutUtils";
import ColorSchemeModal from "../../modals/ColorSchemeModal";
import ShadowEditorModal from "../../modals/ShadowEditorModal";
import Button from "../../ui/Button";
import ColorInput from "../../ui/ColorInput";
import UnitInput from "../../ui/UnitInput";

interface HeaderSettingsProps {
	state: HeaderState;
	dispatch: React.Dispatch<HeaderAction>;
}

const HeaderSettings: React.FC<HeaderSettingsProps> = ({ state, dispatch }) => {
	// State for modals
	const [showShadowEditor, setShowShadowEditor] = useState(false);
	const [showColorSchemeModal, setShowColorSchemeModal] = useState(false);

	// Set header type (card or full)
	const headerType = state.styles.maxWidth ? "card" : "full";

	// Handle header type change
	const handleHeaderTypeChange = (type: string) => {
		if (type === "card") {
			// Set default card styles if switching to card
			dispatch({
				type: "UPDATE_HEADER_STYLES",
				payload: {
					...state.styles,
					maxWidth: "1200px",
					width: "100%",
					margin: "0 auto",
					borderRadius: "8px",
				},
			});
		} else {
			// Remove card-specific styles
			const { maxWidth, borderRadius, margin, ...otherStyles } =
				state.styles;
			dispatch({
				type: "UPDATE_HEADER_STYLES",
				payload: otherStyles,
			});
		}
	};

	// Apply color scheme
	const applyColorScheme = (scheme: any) => {
		// Update header background
		dispatch({
			type: "UPDATE_HEADER_STYLES",
			payload: {
				background: scheme.background,
				color: scheme.text,
				borderColor: scheme.border,
			},
		});

		// Update all button/icon components
		state.components.forEach((comp) => {
			if (["wishlist", "cart", "user", "menu"].includes(comp.type)) {
				dispatch({
					type: "UPDATE_COMPONENT",
					payload: {
						id: comp.id,
						updates: {
							settings: {
								...comp.settings,
								background: scheme.iconBackground,
								hoverColor: scheme.hoverColor,
							},
							styles: {
								...comp.styles,
								color: scheme.iconColor,
							},
						},
					},
				});
			} else if (comp.type === "navItems") {
				dispatch({
					type: "UPDATE_COMPONENT",
					payload: {
						id: comp.id,
						updates: {
							styles: {
								...comp.styles,
								color: scheme.text,
							},
							settings: {
								...comp.settings,
								hoverColor: scheme.hoverText,
							},
						},
					},
				});
			}
		});

		setShowColorSchemeModal(false);
	};

	return (
		<div className="p-4">
			<h3 className="text-lg font-medium mb-4">Header Settings</h3>

			<div className="mb-6">
				<h4 className="text-md font-medium mb-2">Main Settings</h4>

				<div className="mb-4">
					<label className="block text-sm font-medium mb-2">
						Container Type
					</label>
					<div className="flex gap-2 mb-2">
						<Button
							variant={
								state.containerType === "full"
									? "primary"
									: "outline"
							}
							onClick={() =>
								dispatch({
									type: "UPDATE_HEADER_SETTINGS",
									payload: { containerType: "full" },
								})
							}>
							Full Width
						</Button>
						<Button
							variant={
								state.containerType === "container"
									? "primary"
									: "outline"
							}
							onClick={() =>
								dispatch({
									type: "UPDATE_HEADER_SETTINGS",
									payload: { containerType: "container" },
								})
							}>
							Container
						</Button>
					</div>
				</div>

				<div className="mb-4">
					<label className="flex items-center gap-2 text-sm font-medium mb-1">
						<input
							type="checkbox"
							className="w-4 h-4"
							checked={state.isSticky}
							onChange={(e) =>
								dispatch({
									type: "UPDATE_HEADER_SETTINGS",
									payload: { isSticky: e.target.checked },
								})
							}
						/>
						Sticky Header
					</label>
				</div>

				<div className="mb-4">
					<label className="block text-sm font-medium mb-2">
						Header Type
					</label>
					<div className="flex gap-2 mb-2">
						<Button
							variant={
								headerType === "full" ? "primary" : "outline"
							}
							onClick={() => handleHeaderTypeChange("full")}>
							Default (Full)
						</Button>
						<Button
							variant={
								headerType === "card" ? "primary" : "outline"
							}
							onClick={() => handleHeaderTypeChange("card")}>
							Card
						</Button>
					</div>
				</div>

				{/* Card settings */}
				{headerType === "card" && (
					<div className="bg-gray-50 p-3 rounded mb-4">
						<h5 className="text-sm font-medium mb-2">
							Card Settings
						</h5>
						<div className="grid grid-cols-2 gap-3">
							<UnitInput
								label="Width"
								value={state.styles.width || "100%"}
								onChange={(val) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: { width: val },
									})
								}
							/>

							<UnitInput
								label="Max Width"
								value={state.styles.maxWidth || "1200px"}
								onChange={(val) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: { maxWidth: val },
									})
								}
							/>

							<UnitInput
								label="Min Width"
								value={state.styles.minWidth || "auto"}
								onChange={(val) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: { minWidth: val },
									})
								}
								units={["px", "rem", "em", "%", "auto"]}
							/>

							<UnitInput
								label="Border Radius"
								value={state.styles.borderRadius || "8px"}
								onChange={(val) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: { borderRadius: val },
									})
								}
							/>

							<UnitInput
								label="Margin Top/Bottom"
								value={state.styles.marginTop || "0px"}
								onChange={(val) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: {
											marginTop: val,
											marginBottom: val,
										},
									})
								}
							/>

							<UnitInput
								label="Margin Left/Right"
								value={state.styles.marginLeft || "auto"}
								onChange={(val) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: {
											marginLeft: val,
											marginRight: val,
										},
									})
								}
								units={["px", "rem", "em", "%", "auto"]}
							/>
						</div>
					</div>
				)}
			</div>

			<div className="mb-6">
				<h4 className="text-md font-medium mb-2">Appearance</h4>

				<div className="mb-4">
					<div className="flex justify-between items-center mb-2">
						<label className="block text-sm font-medium">
							Color Scheme
						</label>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowColorSchemeModal(true)}>
							Edit Color Scheme
						</Button>
					</div>
				</div>

				<div className="mb-4">
					<ColorInput
						label="Background Color"
						value={state.styles.background || "#ffffff"}
						onChange={(val) =>
							dispatch({
								type: "UPDATE_HEADER_STYLES",
								payload: { background: val },
							})
						}
						withOpacity={true}
					/>
				</div>

				<div className="mb-4">
					<div className="flex justify-between items-center mb-2">
						<label className="block text-sm font-medium">
							Box Shadow
						</label>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowShadowEditor(true)}>
							Edit Shadow
						</Button>
					</div>
					<div className="text-sm bg-gray-50 p-2 rounded border">
						{state.styles.boxShadow || "none"}
					</div>
				</div>

				<div className="mb-4">
					<UnitInput
						label="Padding"
						value={state.styles.padding || "16px"}
						onChange={(val) =>
							dispatch({
								type: "UPDATE_HEADER_STYLES",
								payload: { padding: val },
							})
						}
					/>
				</div>

				<div className="mb-4">
					<label className="block text-sm font-medium mb-1">
						Border
					</label>
					<div className="grid grid-cols-2 gap-3">
						<UnitInput
							value={state.styles.borderWidth || "0px"}
							onChange={(val) =>
								dispatch({
									type: "UPDATE_HEADER_STYLES",
									payload: { borderWidth: val },
								})
							}
							placeholder="Width"
							units={["px", "rem", "em"]}
							min={0}
						/>

						<select
							className="p-2 border rounded"
							value={state.styles.borderStyle || "solid"}
							onChange={(e) =>
								dispatch({
									type: "UPDATE_HEADER_STYLES",
									payload: { borderStyle: e.target.value },
								})
							}>
							<option value="solid">Solid</option>
							<option value="dashed">Dashed</option>
							<option value="dotted">Dotted</option>
							<option value="double">Double</option>
							<option value="none">None</option>
						</select>
					</div>

					<div className="mt-2">
						<ColorInput
							value={state.styles.borderColor || "#e5e7eb"}
							onChange={(val) =>
								dispatch({
									type: "UPDATE_HEADER_STYLES",
									payload: { borderColor: val },
								})
							}
							label="Border Color"
						/>
					</div>
				</div>
			</div>

			<div className="mb-4">
				<label className="block text-sm font-medium mb-1">Layout</label>
				<div className="grid grid-cols-2 gap-2 mb-3">
					{[
						"standard",
						"centered",
						"split",
						"logo-left",
						"logo-right",
						"three-columns",
						"stacked",
						"minimalist",
						"dual-row",
						"asymmetric",
						"multirow",
						"compact",
						"overlay",
						"logo-centered-nav-below",
					].map((layoutOption) => (
						<div
							key={layoutOption}
							className={`border p-2 cursor-pointer rounded ${
								state.layout === layoutOption
									? "border-blue-500 bg-blue-50"
									: "border-gray-300"
							}`}
							onClick={() =>
								dispatch({
									type: "UPDATE_LAYOUT",
									payload: layoutOption,
								})
							}>
							<div className="text-xs text-center mb-1 font-medium">
								{layoutOption
									.replace(/-/g, " ")
									.replace(/\b\w/g, (c) => c.toUpperCase())}
							</div>
							<div className="h-10 border bg-white rounded flex items-center px-1">
								{renderLayoutPreview(layoutOption)}
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Shadow Editor Modal */}
			{showShadowEditor && (
				<ShadowEditorModal
					value={state.styles.boxShadow || "none"}
					onChange={(val) =>
						dispatch({
							type: "UPDATE_HEADER_STYLES",
							payload: { boxShadow: val },
						})
					}
					onClose={() => setShowShadowEditor(false)}
				/>
			)}

			{/* Color Scheme Modal */}
			{showColorSchemeModal && (
				<ColorSchemeModal
					onApply={applyColorScheme}
					onClose={() => setShowColorSchemeModal(false)}
					currentColors={{
						background: state.styles.background,
						text: state.styles.color,
						border: state.styles.borderColor,
						// We'll just use the first icon component's colors as current values
						iconColor: state.components.find((c) =>
							["wishlist", "cart", "user", "menu"].includes(
								c.type
							)
						)?.styles?.color,
						iconBackground: state.components.find((c) =>
							["wishlist", "cart", "user", "menu"].includes(
								c.type
							)
						)?.settings?.background,
						hoverColor: state.components.find((c) =>
							["wishlist", "cart", "user", "menu"].includes(
								c.type
							)
						)?.settings?.hoverColor,
						hoverText: state.components.find(
							(c) => c.type === "navItems"
						)?.settings?.hoverColor,
					}}
				/>
			)}
		</div>
	);
};

export default HeaderSettings;
